import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Check, X, FileDown } from "lucide-react";

const openProof = async (fileId, name) => {
  try {
    const r = await api.get(`/files/${fileId}/download`, { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url; a.download = name || "bank-proof"; a.click();
    URL.revokeObjectURL(url);
  } catch { toast.error("Could not open proof"); }
};

const RejectDialog = ({ req, onClose, onDone }) => {
  const [reason, setReason] = useState("");
  const go = async () => {
    try { await api.post(`/v2/bank-requests/${req.id}/reject`, { reason }); toast.success("Rejected"); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || "Reject failed"); }
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="bank-reject-dialog">
        <DialogHeader><DialogTitle>Reject bank details · {req.user_name}</DialogTitle></DialogHeader>
        <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (shown to the member)" data-testid="bank-reject-reason" />
        <DialogFooter><Button variant="destructive" disabled={!reason.trim()} onClick={go} data-testid="bank-reject-confirm">Reject</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const RequestRow = ({ r, onVerify, onReject }) => (
  <tr className="border-t" data-testid={`bank-req-row-${r.id}`}>
    <td className="p-2">{r.user_name}<div className="text-[10px] text-slate-500">{r.employee_code || ""} {r.user_email}</div></td>
    <td className="p-2">{r.bank_name}<div className="font-mono text-[11px]">{r.account_number} · {r.ifsc}</div>{r.pan && <div className="text-[10px] text-slate-500">PAN {r.pan}</div>}</td>
    <td className="p-2 text-[11px] text-slate-500">{r.current?.account_masked ? `${r.current.bank_name} ${r.current.account_masked} · ${r.current.ifsc}` : "None"}</td>
    <td className="p-2"><Button size="sm" variant="ghost" onClick={() => openProof(r.proof_file_id, r.proof_filename)} data-testid={`bank-req-proof-${r.id}`}><FileDown className="w-3.5 h-3.5 mr-1" /> Proof</Button></td>
    <td className="p-2">{(r.created_at || "").slice(0, 10)}</td>
    <td className="p-2 text-right whitespace-nowrap">
      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 mr-1" onClick={() => onVerify(r)} data-testid={`bank-req-verify-${r.id}`}><Check className="w-3.5 h-3.5 mr-1" /> Verify</Button>
      <Button size="sm" variant="outline" onClick={() => onReject(r)} data-testid={`bank-req-reject-${r.id}`}><X className="w-3.5 h-3.5 mr-1" /> Reject</Button>
    </td>
  </tr>
);

export const BankRequestsTab = () => {
  const [rows, setRows] = useState(null);
  const [rejecting, setRejecting] = useState(null);
  const load = async () => { const r = await api.get("/v2/bank-requests"); setRows(r.data); };
  useEffect(() => { load(); }, []);
  const verify = async (r) => {
    if (!window.confirm(`Verify bank details for ${r.user_name}? They become the official payee details.`)) return;
    try { await api.post(`/v2/bank-requests/${r.id}/verify`); toast.success("Verified"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Verify failed"); }
  };
  if (!rows) return <div className="text-sm text-slate-500">Loading…</div>;
  return (
    <Card data-testid="bank-requests-tab">
      <CardContent className="p-3">
        <div className="text-sm text-slate-600 mb-2" data-testid="bank-requests-count">{rows.length} submission(s) awaiting verification. Check the proof matches before verifying.</div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-left text-slate-500"><tr>{["Member", "Submitted details", "Current verified", "Proof", "Submitted", ""].map((h) => <th key={h} className="p-2 font-medium">{h}</th>)}</tr></thead>
            <tbody>{rows.map((r) => <RequestRow key={r.id} r={r} onVerify={verify} onReject={setRejecting} />)}</tbody>
          </table>
        </div>
        {rows.length === 0 && <div className="text-sm text-emerald-700 p-2" data-testid="bank-requests-empty"><Badge className="bg-emerald-600 mr-1">All clear</Badge>Nothing to verify.</div>}
        {rejecting && <RejectDialog req={rejecting} onClose={() => setRejecting(null)} onDone={() => { setRejecting(null); load(); }} />}
      </CardContent>
    </Card>
  );
};
